import { App } from '@capacitor/app';
import { supabase } from './supabaseClient';
import { isNativePlatform, getPlatform } from './platformUtils';

/**
 * Compare two dotted version strings by numeric segments.
 * Missing/invalid segments are treated as 0, so "1.0.2" < "1.0.12".
 * @returns {-1|0|1} -1 if a < b, 1 if a > b, 0 if equal
 */
export function compareVersions(a, b) {
  const pa = String(a ?? '').trim().split('.');
  const pb = String(b ?? '').trim().split('.');
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = parseInt(pa[i], 10) || 0;
    const nb = parseInt(pb[i], 10) || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

/**
 * Installed native app version (Android versionName / iOS CFBundleShortVersionString).
 * Returns null on web or if the native call fails.
 */
export async function getInstalledVersion() {
  if (!isNativePlatform()) return null;
  try {
    const info = await App.getInfo();
    return info?.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Decide whether the user must / should update the app.
 * Fail-open by design: web, native errors, missing/disabled config, or query
 * failures all resolve to { status: 'ok' } so an outage never locks users out.
 *
 * @returns {Promise<{status:'ok'|'recommended'|'required', storeUrl?:string,
 *   latestVersion?:string, minVersion?:string, message?:string}>}
 */
export async function checkAppVersion() {
  const OK = { status: 'ok' };
  try {
    if (!isNativePlatform()) return OK;

    const installed = await getInstalledVersion();
    if (!installed) return OK;

    const platform = getPlatform(); // 'ios' | 'android'
    const { data, error } = await supabase
      .from('app_version')
      .select('min_version, latest_version, store_url, update_message, is_active')
      .eq('platform', platform)
      .single();

    if (error || !data || !data.is_active) return OK;

    const base = {
      storeUrl: data.store_url,
      latestVersion: data.latest_version,
      minVersion: data.min_version,
      message: data.update_message,
    };

    if (data.min_version && compareVersions(installed, data.min_version) < 0) {
      return { status: 'required', ...base };
    }
    if (data.latest_version && compareVersions(installed, data.latest_version) < 0) {
      return { status: 'recommended', ...base };
    }
    return OK;
  } catch {
    return OK;
  }
}
